import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, ChevronUp, ChevronDown, Layers } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger } from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";

const inputCls =
  "rounded-none border-[#D4D4D4] dark:border-[#1F1F1F] dark:bg-[#0A0A0A] dark:text-white focus-visible:ring-0 focus-visible:border-[#0A0A0A] dark:focus-visible:border-[var(--neon-cyan)] h-9 font-mono text-sm";

export default function LayerBuilder({ layers, setLayers, materials, conditions }) {
  const { t } = useI18n();
  const isLight = conditions?._mode === "day";
  // Unified vivid layer-list palette per theme.
  // Light = TEAL #0891B2 (exact match with "+Add Layer" button).
  // Dark  = bright white for harmony with the teal button in dark theme.
  const headingColor  = isLight ? "#0891B2" : "#FFFFFF";
  const materialColor = isLight ? "#0891B2" : "#FFFFFF";
  const addLayer = () => {
    setLayers([...layers, {
      id: crypto.randomUUID(),
      name: t("layers.newLayer", "New layer"),
      thickness: 0.05,
      lambda_value: 0.04,
      mu: 1,
      brand: "",
    }]);
  };

  const update = (id, key, value) => {
    setLayers(layers.map((l) =>
      l.id === id ? { ...l, [key]: key === "name" || key === "brand" ? value : Number(value) } : l
    ));
  };

  const remove = (id) => setLayers(layers.filter((l) => l.id !== id));
  const move = (idx, dir) => {
    const next = [...layers];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setLayers(next);
  };

  const applyMaterial = (id, mName) => {
    const m = materials.find((mm) => mm.name === mName);
    if (!m) return;
    setLayers(layers.map((l) => l.id === id
      ? { ...l, name: m.name, lambda_value: m.lambda_value, mu: m.mu ?? 1, brand: m.brand || "" }
      : l));
  };

  return (
    <div data-testid="layer-builder">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2"
          style={{ color: headingColor }}>
          <Layers className="w-5 h-5" style={{ color: headingColor }}/>
          {t("layers.title", "Layers (Inside → Outside)")}
        </h2>
        <Button onClick={addLayer}
          className="rounded-none bg-[#0A0A0A] hover:bg-[#333333] text-white h-9 font-mono text-xs uppercase tracking-wider"
          data-testid="add-layer-btn">
          <Plus className="w-4 h-4 mr-1"/> {t("layers.add", "Layer")}
        </Button>
      </div>

      <div className="border border-[#D4D4D4] dark:border-[#1F1F1F]">
        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-[#F2F2F2] dark:bg-[#0A0A0A] tech-label border-b border-[#D4D4D4] dark:border-[#1F1F1F]">
          <div className="col-span-1">#</div>
          <div className="col-span-4">{t("layers.material", "MATERIAL")}</div>
          <div className="col-span-2">{t("layers.thickness", "d (m)")}</div>
          <div className="col-span-2">{t("layers.lambda", "λ")}</div>
          <div className="col-span-1">μ</div>
          <div className="col-span-2 text-right">{t("layers.action", "ACTION")}</div>
        </div>

        {layers.length === 0 && (
          <div className="p-6 text-center text-sm text-[#52525B] font-mono">
            {t("layers.empty", "No layers yet. Start with + Layer.")}
          </div>
        )}

        {layers.map((l, idx) => (
          <div key={l.id}
            className="grid grid-cols-12 gap-2 items-center px-3 py-2 border-b border-[#D4D4D4] dark:border-[#1F1F1F] last:border-b-0 hover:bg-[#F9F9FA] dark:hover:bg-[#0F0F0F]"
            data-testid={`layer-row-${idx}`}>
            <div className="col-span-1 font-mono text-xs text-[#52525B]">L{idx + 1}</div>
            <div className="col-span-4">
              <Select onValueChange={(v) => applyMaterial(l.id, v)}>
                <SelectTrigger className={inputCls}
                  style={{ color: materialColor, fontWeight: 600 }}
                  data-testid={`layer-mat-select-${idx}`}>
                  <span style={{ color: materialColor, fontWeight: 600 }}>{l.name}</span>
                </SelectTrigger>
                <SelectContent className="rounded-none max-h-72">
                  {materials.map((m) => (
                    <SelectItem key={m.id} value={m.name} className="rounded-none font-mono text-xs">
                      {m.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Input type="number" step="0.001" min="0" className={inputCls}
                value={l.thickness}
                onChange={(e) => update(l.id, "thickness", e.target.value)}
                data-testid={`layer-thickness-${idx}`}/>
            </div>
            <div className="col-span-2">
              <Input type="number" step="0.001" min="0" className={inputCls}
                value={l.lambda_value}
                onChange={(e) => update(l.id, "lambda_value", e.target.value)}
                data-testid={`layer-lambda-${idx}`}/>
            </div>
            <div className="col-span-1">
              <Input type="number" step="0.5" min="0" className={inputCls}
                value={l.mu ?? 1}
                onChange={(e) => update(l.id, "mu", e.target.value)}
                data-testid={`layer-mu-${idx}`}/>
            </div>
            <div className="col-span-2 flex items-center justify-end gap-1">
              <button onClick={() => move(idx, -1)} disabled={idx === 0}
                className="p-1 hover:bg-[#F2F2F2] disabled:opacity-30" data-testid={`layer-up-${idx}`}>
                <ChevronUp className="w-4 h-4"/>
              </button>
              <button onClick={() => move(idx, 1)} disabled={idx === layers.length - 1}
                className="p-1 hover:bg-[#F2F2F2] disabled:opacity-30" data-testid={`layer-down-${idx}`}>
                <ChevronDown className="w-4 h-4"/>
              </button>
              <button onClick={() => remove(l.id)}
                className="p-1 hover:bg-[#FFE5E3] text-[#FF3B30]" data-testid={`layer-del-${idx}`}>
                <Trash2 className="w-4 h-4"/>
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
